import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  CategoryPageBelowListEntry,
  CategoryPageSubheaderEntry,
  CustomTagFilterConfig,
  ListPageEntry,
  MenuContext,
  ModuleRegistryEntry,
  RoutablePageRegistryEntry,
  StoreFrontCarouselTitleProps,
  StoreFrontCarouselTitleRegistryEntry,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import type { Dispensaries, RemoteExtensionsConfiguration } from 'src/gql/graphql';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useLazyRemoteModule from 'src/utils/module-federation/use-lazy-remote-module';
import useUI from 'hooks/use-ui';
import { useRouter } from 'next/compat/router';
import type { CategoryPageBelowListIterable } from 'src/dispensary/category/category-page-below-list';

import { getLivePreviewConfig } from 'src/utils/module-federation/get-live-preview-config';
import { isCheckoutOwnedRoute, isStorefrontCheckoutThemeRoute } from 'src/checkout/theme/checkout-theme-routes';
import { remoteComponentIsValid } from 'src/theme/remote-component-is-valid';

import {
  CommerceComponents,
  CustomComponentsRegistry,
  ExtendedRemoteModuleRegistry,
  RemoteTheme,
  RouteablePage,
  defaultCommerceComponents,
  CustomSmartTagFilterConfig,
} from './context';
import { useCommerceBridge } from './bridge/bridge-context';
import { useRegistryTheme } from './registry-theme';

export { remoteComponentIsValid };

// Becomes the CommerceComponentsContext value for the majority of stores, so it must be
// one shared reference — a fresh spread per call re-renders every consumer on unrelated
// churn. Resolved is true because there is no remote to wait for.
/**
 * Validates federated custom tag filter configurations at runtime before passing to consumers.
 * - Value must be an array; otherwise returns [].
 * - Each entry must be a plain object with non-empty string `category`, `name`, `title`
 *   and a non-empty `match` array containing non-empty strings.
 * - Malformed entries are dropped without throwing.
 * - Duplicate categories within the same source: first valid entry wins.
 */
export function validateCustomTagFilterConfigs<T extends CustomSmartTagFilterConfig | CustomTagFilterConfig>(
  raw: unknown
): T[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const seenCategories = new Set<string>();
  const validConfigs: T[] = [];

  raw.forEach((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return;
    }

    const { category, name, title, match } = item as Record<string, unknown>;

    if (
      typeof category !== 'string' ||
      category.trim() === '' ||
      typeof name !== 'string' ||
      name.trim() === '' ||
      typeof title !== 'string' ||
      title.trim() === '' ||
      !Array.isArray(match) ||
      match.length === 0 ||
      !match.every((m) => typeof m === 'string' && m.trim().length > 0)
    ) {
      return;
    }

    if (seenCategories.has(category)) {
      return;
    }

    seenCategories.add(category);
    validConfigs.push({
      category,
      name,
      title,
      match: [...match],
    } as T);
  });

  return validConfigs;
}

/**
 * A select/radio option is only usable once its `value` reaches `sdk-custom-components.tsx`'s
 * `withAutoFieldAiSchema`, which keys the generated `ai.schema`'s JSON Schema `type` off
 * `typeof option.value` -- `object`/`undefined` aren't valid JSON Schema types at all, and a
 * `null` value would produce a schema matching nothing. Checking only `'value' in option` (as
 * this used to) would let any of those through, silently reaching Puck's AI plugin with a
 * malformed schema despite this validator's own fails-closed contract. `label` is required
 * (non-optional) on `CustomComponentParamField`'s option shape, so it's checked too.
 *
 * `typeof value === 'number'` alone isn't enough: `NaN` and `Infinity` both pass that check but
 * aren't valid JSON (`JSON.stringify(NaN)`/`JSON.stringify(Infinity)` both serialize to `null`),
 * so either would silently become a `null` entry in the derived `ai.schema` enum -- the exact
 * malformed-schema case this validator exists to stop. `Number.isFinite` rejects both while still
 * accepting `0` and negative numbers.
 */
// Puck's own `ComponentProps`/`AsFieldProps` machinery reserves these as special prop names
// (`actions-DA1J5F56.d.ts`): `puck` and `editMode` are injected by Puck itself (and `puck` is
// already stripped by `sdk-custom-components.tsx`'s `render` destructure, so a remote theme
// declaring it would just silently get nothing back); `children` is Puck's own slot-content
// prop. `id` isn't part of that type-level list, but it's the actual prop Puck's runtime uses
// for a block's instance identity (selection, drag, slot bookkeeping) -- a theme-declared `id`
// param would let an author's own field edits corrupt that bookkeeping or collide two blocks
// onto one id. None of these are safe to expose as an author-editable param.
const RESERVED_SDK_COMPONENT_PARAM_NAMES = new Set(['id', 'puck', 'editMode', 'children']);

function isValidCustomComponentSelectOption(option: unknown): boolean {
  if (!option || typeof option !== 'object' || Array.isArray(option)) {
    return false;
  }

  const { label, value } = option as Record<string, unknown>;
  return (
    typeof label === 'string' &&
    (typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value)))
  );
}

/**
 * Validates a federated `CustomComponents` registry at runtime before it reaches the CMS
 * editor's palette-building code.
 * - Value must be a plain object (not an array); otherwise returns `{}`.
 * - Each entry must itself be a plain object; non-object entries are dropped.
 * - `displayName`/`description` are kept only when non-empty strings, otherwise omitted --
 *   consumers fall back to the registry key for the label.
 * - `params`, when present, must be a plain object; each param entry is kept only when it has one
 *   of the supported field `type`s (text/textarea/number/select/radio) and, for select/radio, a
 *   non-empty `options` array of well-formed `{ label: string, value: boolean | number | string }`
 *   entries (see `isValidCustomComponentSelectOption`). Malformed params are dropped individually
 *   rather than discarding the whole component.
 * - A param keyed by a name Puck itself reserves (`id`, `puck`, `editMode`, `children` -- see
 *   `RESERVED_SDK_COMPONENT_PARAM_NAMES`) is dropped outright, regardless of its field `type`,
 *   since an author-editable field bound to one of those props can corrupt Puck's own instance
 *   bookkeeping rather than just the remote component's own data.
 * - `component` is passed through as-is -- its own validity (DataBridgeVersion compatibility) is
 *   checked later, at render time, by `remoteComponentIsValid`, same as every other slot.
 */
export function validateCustomComponentsRegistry(raw: unknown): CustomComponentsRegistry {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const validated: CustomComponentsRegistry = {};

  Object.entries(raw as Record<string, unknown>).forEach(([id, entry]) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return;
    }

    const { component, displayName, description, params } = entry as Record<string, unknown>;

    const validatedParams: Record<string, unknown> = {};
    if (params && typeof params === 'object' && !Array.isArray(params)) {
      Object.entries(params as Record<string, unknown>).forEach(([paramKey, paramValue]) => {
        if (RESERVED_SDK_COMPONENT_PARAM_NAMES.has(paramKey)) {
          return;
        }
        if (!paramValue || typeof paramValue !== 'object' || Array.isArray(paramValue)) {
          return;
        }
        const { type, options } = paramValue as Record<string, unknown>;
        if (type === 'text' || type === 'textarea' || type === 'number') {
          validatedParams[paramKey] = paramValue;
          return;
        }
        if (
          (type === 'select' || type === 'radio') &&
          Array.isArray(options) &&
          options.length > 0 &&
          options.every((option) => isValidCustomComponentSelectOption(option))
        ) {
          validatedParams[paramKey] = paramValue;
        }
      });
    }

    validated[id] = {
      ...(component !== undefined ? { component: component as CustomComponentsRegistry[string]['component'] } : {}),
      ...(typeof displayName === 'string' && displayName.trim() !== '' ? { displayName } : {}),
      ...(typeof description === 'string' && description.trim() !== '' ? { description } : {}),
      ...(Object.keys(validatedParams).length > 0
        ? { params: validatedParams as CustomComponentsRegistry[string]['params'] }
        : {}),
    };
  });

  return validated;
}

const resolvedDefaultCommerceComponents: CommerceComponents = {
  ...defaultCommerceComponents,
  isRemoteThemeResolved: true,
};

type RemoteCompositionProps = {
  component: React.ReactElement;
  renderRemote?: (component: React.ReactElement) => React.ReactElement;
};

function RemoteComposition({ component, renderRemote }: RemoteCompositionProps): React.ReactElement {
  return renderRemote ? renderRemote(component) : component;
}

// Reserved heights mirror the stock fallback blocks (DefaultHeader 72px, desktop nav
// min-height 68px) so held header-block slots keep their footprint while a remote
// theme loads and content below doesn't reflow when the theme mounts.
const STOREFRONT_HEADER_SKELETON_MIN_HEIGHT = 'var(--dcm-storefront-header-height, 72px)';
const STOREFRONT_NAVIGATION_SKELETON_MIN_HEIGHT = 'var(--dcm-header-min-height, 68px)';

const ThemeSlotSkeleton: React.FC<{ minHeight: string }> = ({ minHeight }) => (
  <div data-testid='remote-theme-slot-skeleton' style={{ minHeight }} />
);

export function useRemoteCommerceComponents(): {
  getCommerceComponents: (styledTheme?: RemoteTheme) => CommerceComponents;
  remoteThemeModule: any;
  modulePath: string | undefined;
  isRemoteThemeResolved: boolean;
  shouldProvideCommerceBridge: boolean;
} {
  const [moduleLoadError, setModuleLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const UI = useUI();
  const router = useRouter();
  const { isStoreFront, isKiosk } = UI;
  const isCheckoutOwnedPage = isCheckoutOwnedRoute(router?.route);
  const isStorefrontCheckoutPage = isStorefrontCheckoutThemeRoute(router?.route);
  const isIsolatedPlusCheckoutPage = isCheckoutOwnedPage && !isStorefrontCheckoutPage;
  const isStoreFrontOrKiosk = !isIsolatedPlusCheckoutPage && (isStoreFront || isKiosk);

  const { dispensary: currentDispensary } = useCurrentDispensary();
  const { dispensary: legacyDispensary } = useDispensary() as { dispensary?: Dispensaries | undefined };
  const dispensary = currentDispensary ?? legacyDispensary;
  const rawPath = typeof router?.asPath === 'string' ? router.asPath : '';
  const pathWithoutHash = rawPath.split('#')[0];
  const currentSearchString = pathWithoutHash.includes('?') ? pathWithoutHash.split('?')[1] : undefined;

  const [livePreviewConfig, setLivePreviewConfig] = useState<RemoteExtensionsConfiguration | null>(() =>
    getLivePreviewConfig(currentSearchString)
  );

  useEffect(() => {
    const nextConfig = getLivePreviewConfig(currentSearchString);
    if (nextConfig) {
      setLivePreviewConfig(nextConfig);
    }
  }, [currentSearchString]);

  // Use window.reactEnv as an early source for the theme config. It's server-injected
  // before React boots, so we know immediately whether a remote theme is configured —
  // without waiting for the dispensary Apollo query to complete.
  const reactEnvThemeConfig = useMemo(
    () => (typeof window !== 'undefined' ? window.reactEnv?.remoteExtensions?.theme : null),
    []
  );
  // Once dispensary has loaded, trust only its data. Before it loads, seed from
  // window.reactEnv so we know a remote is coming before the query round-trip.
  const dispensaryOrEarlyTheme = dispensary ? dispensary.remoteExtensions?.theme : reactEnvThemeConfig;

  // ECOMM-14489 / NOTE ON SCOPE: an earlier revision of this PR made this hook prefer
  // `UI.websiteTheme.extensions` (the website-level remote SDK theme config) over the
  // dispensary-level config IN THE CMS EDITOR specifically (`UI.isCMS`), so the editor's "SDK
  // Components" palette would reflect the website being edited rather than whichever dispensary
  // the "Previewing as" switcher happened to have selected. Three rounds of review established
  // that this cannot be done correctly in isolation: NO production CMS renderer route
  // (`pages/stores/[cName]/cms/[...slug].tsx`, `pages/[...slug].tsx`, and a not-yet-mounted
  // menu-landing renderer) resolves SDK Components from a website-level theme today, and all
  // three share this exact hook via `useCmsConfigWithSdkComponents`/`useCommerceComponents` for
  // their own `customComponents`. Making ONLY the editor prefer the website-level config
  // necessarily creates a case where an author places/previews a website-registry block that
  // will never resolve in production (the reverse of the bug this ticket set out to fix), or --
  // if a dispensary-level fallback is added to keep the editor's EMPTY-website-config case
  // matching production -- the editor and production permanently disagree the moment a website
  // DOES configure a loadable `extensions` config, since production never consults it. Fixing
  // that for real requires every one of these routes to resolve a website-level theme the same
  // way, which is exactly the broader, cross-route precedence work the separate, still-under-
  // review GetDutchie/Dutchie#24508 is stuck in review over -- out of scope for this PR to take
  // on unilaterally. This hook therefore deliberately still resolves the SAME dispensary-level
  // config everywhere, including the CMS editor, matching `develop` -- `UI.websiteTheme` is not
  // read here at all. The GraphQL/type groundwork this PR adds for `extensions`
  // (`use-website.ts`/`website-theme-variables.tsx`) and the ported, already-reviewed
  // `isAllowedWebsiteThemeManifestUrl` security allowlist (`get-live-preview-config.ts`) are
  // left in place as inert, ready-to-use infrastructure for whichever follow-up (likely #24508
  // itself, or a ticket that lands alongside it) updates every one of these render paths at once.

  // Isolated Plus checkout remains fully native. Pro storefront checkout preserves the
  // root theme's pre-existing GlobalStyles only; getCommerceComponents projects every
  // executable storefront slot, event, page, iterable, and theme config back to defaults.
  const customizationProvider = isIsolatedPlusCheckoutPage
    ? null
    : livePreviewConfig ?? (isStoreFrontOrKiosk ? dispensaryOrEarlyTheme : null);

  const isServer = typeof window === 'undefined';
  const { status: registryStatus, theme: prewarmedTheme } = useRegistryTheme({
    hasLivePreviewManifest: Boolean(livePreviewConfig?.manifestUrl),
    modulePath: customizationProvider?.modulePath,
    scope: customizationProvider?.scope,
  });
  const isRegistryLoadPending = registryStatus === 'loading';

  // Whether a remote theme is configured for this store. This is knowable during SSR: the
  // dispensary config (including remoteExtensions.theme) is prefetched into the Apollo cache
  // server-side, and window.reactEnv.remoteExtensions.theme is injected before React boots on the
  // client. Because both the server render and the first client render derive the same value, we
  // can seed render state from it without risking a hydration mismatch.
  const isThemeExpected = Boolean(customizationProvider?.modulePath);

  // Holds theme component slots blank until the initial remote fetch settles, preventing the stock
  // Dutchie UI from painting before the customer theme arrives. Module federation is browser-only,
  // so the remote can never resolve during SSR; seeding from isThemeExpected makes the server itself
  // hold the slot (rather than flashing stock UI that the client would immediately replace), and
  // keeps the first client render identical to the server HTML for clean hydration. The client then
  // swaps in the real remote once loadRemote resolves post-mount.
  const [isPendingInitialLoad, setIsPendingInitialLoad] = useState<boolean>(
    Boolean(!prewarmedTheme && isThemeExpected)
  );

  const skip = !prewarmedTheme && (isServer || !customizationProvider?.modulePath);
  const skipLazyLoad = skip || Boolean(prewarmedTheme) || isRegistryLoadPending;

  // `modulePath` alone isn't a reliable theme identity: `useLazyRemoteModule` resets whenever
  // ANY of manifestUrl/modulePath/scope/skip changes (see that hook's own effect deps), since
  // two different stores/manifests can legitimately expose the same Module Federation
  // `modulePath` (e.g. the conventional './Theme' expose name) while differing only in
  // manifestUrl/scope. Comparing modulePath alone would miss that switch entirely. Composing all
  // three into one string gives the switch-detection below (and `CommerceComponents
  // .themeIdentity`, exposed to consumers like `useCmsConfigWithSdkComponents`) the same
  // identity `useLazyRemoteModule` itself keys its own reset on.
  const themeIdentity = customizationProvider?.modulePath
    ? JSON.stringify([
        customizationProvider.manifestUrl ?? '',
        customizationProvider.scope ?? '',
        customizationProvider.modulePath,
      ])
    : undefined;

  // `useLazyRemoteModule` only resets `remoteThemeModule` back to its `fallback` for a NEW target
  // inside a `useEffect` (see that hook's own docblock) -- which runs post-commit, one render
  // after `customizationProvider` (and therefore `themeIdentity` above) already reflects the
  // switch. So there is exactly one render, every time the active store's theme target changes,
  // where `themeIdentity` already names the NEW target but `remoteThemeModule` still holds the
  // OLD target's resolved data. Comparing `themeIdentity` to what it was on the PREVIOUS render
  // -- synchronously, during render, not in an effect -- catches exactly that render:
  // `isSwitchingTheme` is true for it and it alone, since by the very next render this ref has
  // already been updated to match. `getCommerceComponents` uses it below to force
  // `customComponents`/`findCustomComponent` to their "still loading" shape for that one render,
  // the same shape they already use while isPendingInitialLoad, so a theme switch can never
  // publish one target's registry mislabeled under a different target's identity.
  //
  // The ref is advanced in an effect, not during render: render-phase state adjustments (e.g. the
  // registry hook's key reset) make React replay this body, and a render-time write would let the
  // replay see the NEW identity as already seen, publishing the old module under it. Passive
  // effects are always flushed before the next render starts, so the guard still lasts one commit.
  const lastSeenThemeIdentityRef = useRef<string | undefined>(themeIdentity);
  const isSwitchingTheme = lastSeenThemeIdentityRef.current !== themeIdentity;
  useEffect(() => {
    lastSeenThemeIdentityRef.current = themeIdentity;
  }, [themeIdentity]);

  // If skip transitions to true after mount (e.g. dispensary loaded with no theme config),
  // release the hold so stock/fallback components can render.
  useEffect(() => {
    if (skip || prewarmedTheme) {
      setIsPendingInitialLoad(false);
    }
    if (prewarmedTheme) {
      setModuleLoadError(null);
      setIsLoading(false);
    }
  }, [prewarmedTheme, skip]);

  const handleLoadFail = useCallback((error: string) => {
    setModuleLoadError(error);
    setIsPendingInitialLoad(false);
  }, []);

  const handleLoading = useCallback((loading: boolean) => {
    if (loading) {
      // Clear any previous error so a successful re-fetch can render the theme.
      setModuleLoadError(null);
    }
    setIsLoading(loading);
    if (!loading) {
      setIsPendingInitialLoad(false);
    }
  }, []);

  const prewarmedModule = useMemo<{ default?: ExtendedRemoteModuleRegistry } | null>(() => {
    if (!prewarmedTheme) return null;
    // Normalize the prewarmed module so it always exposes components under `default`, matching
    // the shape that useLazyRemoteModule resolves to (used for the synchronous SSR path).
    const normalized = ((prewarmedTheme as { default?: ExtendedRemoteModuleRegistry }).default ??
      prewarmedTheme) as ExtendedRemoteModuleRegistry;
    return { default: normalized };
  }, [prewarmedTheme]);

  const lazyRemoteThemeModule = useLazyRemoteModule<{
    default?: ExtendedRemoteModuleRegistry;
  }>({
    manifestUrl: customizationProvider?.manifestUrl ?? '',
    modulePath: customizationProvider?.modulePath ?? '',
    scope: customizationProvider?.scope ?? '',
    fallback: { default: undefined },
    skip: skipLazyLoad,
    onLoadFail: handleLoadFail,
    onLoading: handleLoading,
  });

  const isFederatedThemeSettled = !isSwitchingTheme && Boolean(lazyRemoteThemeModule.default ?? moduleLoadError);

  // When the registry gives up on a theme it was holding, `skipLazyLoad` releases and the federated
  // build starts; re-assert the pending hold so slots never show stock UI in between. Skipped when
  // MF already settled, since raising the hold then would leave a permanent skeleton.
  const [lastRegistryStatus, setLastRegistryStatus] = useState(registryStatus);
  const hasRegistryJustFailed =
    lastRegistryStatus === 'loading' && registryStatus === 'failed' && !isFederatedThemeSettled;
  if (lastRegistryStatus !== registryStatus) {
    setLastRegistryStatus(registryStatus);
    if (hasRegistryJustFailed) {
      setIsPendingInitialLoad(true);
    }
  }

  const remoteThemeModule = useMemo<{ default?: ExtendedRemoteModuleRegistry }>(
    () => prewarmedModule ?? (isSwitchingTheme ? { default: undefined } : lazyRemoteThemeModule),
    [isSwitchingTheme, lazyRemoteThemeModule, prewarmedModule]
  );
  const effectiveModuleLoadError = prewarmedTheme ? null : moduleLoadError;
  const effectiveIsLoading = prewarmedTheme ? false : isLoading;
  const isSwitchingToUnprewarmedTheme = isSwitchingTheme && !prewarmedTheme && isThemeExpected;
  // useLazyRemoteModule calls onLoading(false) whenever it is skipped, which releases
  // isPendingInitialLoad; a pending registry load is its own hold so slots never show stock UI first.
  const effectiveIsPendingLoad =
    isPendingInitialLoad || isSwitchingToUnprewarmedTheme || isRegistryLoadPending || hasRegistryJustFailed;

  const wrapComponentFactory = (
    componentEntry?: ModuleRegistryEntry,
    styledTheme: RemoteTheme = null,
    skeletonMinHeight?: string,
    exposeCommerceBridge = true
  ): ((o?: {
    fallback?: React.FC;
    pending?: React.FC;
    renderRemote?: (component: React.ReactElement) => React.ReactElement;
  }) => React.FC) => {
    if (componentEntry && typeof componentEntry !== 'function') {
      let context: MenuContext | null = null;
      if (isKiosk) {
        context = 'kiosk';
      } else if (isStoreFront) {
        context = 'store-front';
      }
      if (!context) {
        componentEntry = undefined;
      } else {
        componentEntry = componentEntry[context];
      }
    }

    const Component = componentEntry;
    const RemoteComponent = Component as React.FC<any>;

    type WrappedComponentProps = Record<string, unknown> & {
      __remoteFallback: React.FC;
      renderRemoteComponent?: (component: React.ReactElement) => React.ReactElement;
    };

    const RemoteComponentBoundary: React.FC<WrappedComponentProps & { remoteComponent: React.ReactElement }> = ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __remoteFallback: FallbackComponent,
      remoteComponent,
      renderRemoteComponent: renderRemote,
      ...props
    }) => (
      <ErrorBoundary
        fallbackRender={({ error }) => {
          console.error(`Remote Component error (${String(Component?.name)}):`, error);
          return <FallbackComponent {...props} />;
        }}
      >
        <RemoteComposition component={remoteComponent} renderRemote={renderRemote} />
      </ErrorBoundary>
    );

    const BridgedWrappedComponent: React.FC<WrappedComponentProps> = ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __remoteFallback: FallbackComponent,
      renderRemoteComponent,
      ...props
    }) => {
      const remoteData = useCommerceBridge();

      if (!Component || remoteData === null) {
        return <FallbackComponent {...props} />;
      }
      if (remoteData === undefined) {
        return null;
      }

      const remoteComponent = (
        <RemoteComponent theme={styledTheme} __dsdk__dataBridgeData={remoteData} data={remoteData} {...props} />
      );
      return (
        <RemoteComponentBoundary
          __remoteFallback={FallbackComponent}
          remoteComponent={remoteComponent}
          renderRemoteComponent={renderRemoteComponent}
          {...props}
        />
      );
    };

    const BridgeFreeWrappedComponent: React.FC<WrappedComponentProps> = ({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __remoteFallback: FallbackComponent,
      renderRemoteComponent,
      ...props
    }) => {
      if (!Component) {
        return <FallbackComponent {...props} />;
      }

      return (
        <RemoteComponentBoundary
          __remoteFallback={FallbackComponent}
          remoteComponent={<RemoteComponent theme={styledTheme} {...props} />}
          renderRemoteComponent={renderRemoteComponent}
          {...props}
        />
      );
    };

    const WrappedComponent = exposeCommerceBridge ? BridgedWrappedComponent : BridgeFreeWrappedComponent;

    return ({
      fallback = () => <></>,
      pending,
      renderRemote,
    }: {
      fallback?: React.FC;
      pending?: React.FC;
      renderRemote?: (component: React.ReactElement) => React.ReactElement;
    } = {}) => {
      // If there was an error loading the module, return the fallback
      if (effectiveModuleLoadError) {
        return fallback;
      }

      // Hold the slot while the initial remote fetch (or a reload) is in flight so stock
      // Dutchie components don't flash before the theme arrives. Reserved slots keep their
      // height instead of collapsing to zero so content below doesn't reflow on mount.
      if (effectiveIsLoading || effectiveIsPendingLoad) {
        if (pending) {
          return pending;
        }
        if (skeletonMinHeight) {
          return () => <ThemeSlotSkeleton minHeight={skeletonMinHeight} />;
        }
        return () => <></>;
      }

      // If the Component is not defined, return the fallback
      if (!Component) {
        return fallback;
      }

      // If the Component is valid, return it with the fallback bound via props
      if (remoteComponentIsValid(Component)) {
        const BoundComponent: React.FC<any> = (props) => (
          <WrappedComponent __remoteFallback={fallback} renderRemoteComponent={renderRemote} {...props} />
        );
        return BoundComponent;
      }

      // If the component is not valid, return the fallback
      return fallback;
    };
  };

  const findCarouselTitle = (
    entries: Record<string, StoreFrontCarouselTitleRegistryEntry> | undefined,
    carouselId: string,
    styledTheme: RemoteTheme,
    fallback: React.FC<StoreFrontCarouselTitleProps>
  ): React.FC<StoreFrontCarouselTitleProps> => {
    // Carousel-title overrides are optional per carousel. Keep the native title while the
    // registry loads so themes that omit this id never incur a title-sized layout shift.
    if (effectiveModuleLoadError || skip || effectiveIsLoading || effectiveIsPendingLoad) {
      return fallback;
    }

    if (!entries || !Object.prototype.hasOwnProperty.call(entries, carouselId)) {
      return fallback;
    }

    const entry = (entries as Partial<Record<string, StoreFrontCarouselTitleRegistryEntry>>)[carouselId];
    if (!entry) {
      return fallback;
    }

    return wrapComponentFactory(entry, styledTheme)({ fallback }) as React.FC<StoreFrontCarouselTitleProps>;
  };

  const wrapIterables = (
    iterables?: ModuleRegistryEntry[],
    styledTheme: RemoteTheme = null
  ): (() => React.ElementType[]) => () =>
    iterables?.map((iterable) => wrapComponentFactory(iterable, styledTheme)()) ?? [];

  const wrapCategoryIterables = (
    iterables?: ListPageEntry[],
    styledTheme: RemoteTheme = null
  ): (() => { category: ListPageEntry['category']; components: React.ElementType[] }[]) => () =>
    iterables?.map((iterable) => ({
      category: iterable.category,
      components: iterable.components.map((component) => wrapComponentFactory(component, styledTheme)()),
    })) ?? [];

  const wrapCategoryBelowList = (
    entries?: CategoryPageBelowListEntry[],
    styledTheme: RemoteTheme = null
  ): (() => CategoryPageBelowListIterable[]) => () => {
    if (effectiveModuleLoadError || skip || effectiveIsPendingLoad || !Array.isArray(entries)) {
      return [];
    }
    return entries
      .filter((entry): entry is CategoryPageBelowListEntry =>
        Boolean(
          entry &&
            (Boolean(entry.category) || Boolean(entry.subcategory) || Boolean(entry.collection)) &&
            Array.isArray(entry.components)
        )
      )
      .map((entry) => ({
        category: entry.category,
        subcategory: entry.subcategory,
        collection: entry.collection,
        components: entry.components.map((component) => wrapComponentFactory(component, styledTheme)()),
      }));
  };

  // Not a component registry -- returns the raw, unvalidated SDK-authored
  // entries for `CategoryPageSubheaders` as-is. Matching/validation happens
  // in `findMatchingCategorySubheaderText` (category-page-subheaders.ts),
  // called by the host consumer, so no remote-module-loading concerns
  // (skip/error/pending state) leak into that pure helper.
  const getCategorySubheaders = (
    entries?: CategoryPageSubheaderEntry[]
  ): (() => CategoryPageSubheaderEntry[]) => () => {
    if (effectiveModuleLoadError || skip || effectiveIsPendingLoad) {
      return [];
    }
    return entries ?? [];
  };

  const wrapPages = (
    pages: RoutablePageRegistryEntry[] = [],
    styledTheme: RemoteTheme = null,
    fallback: React.FC = () => <></>
  ): RouteablePage[] =>
    pages.map((page) => ({
      ...page,
      component: ({ fallback: pageFallback = fallback } = {}) =>
        wrapComponentFactory(page.component, styledTheme)({ fallback: pageFallback }),
    }));

  const findPage = (
    pagesContainer,
    path = '',
    fallback: React.FC = () => <></>,
    styledTheme: RemoteTheme = null
  ): React.FC => {
    if (effectiveModuleLoadError) {
      return fallback;
    }

    // Precedes `skip` because unprewarmed SSR sets both `skip` and `effectiveIsPendingLoad`,
    // and server/client must both hold blank while the initial remote fetch is pending.
    if (effectiveIsPendingLoad) {
      return () => <></>;
    }

    if (skip) {
      return fallback;
    }

    if (!pagesContainer) {
      return () => <></>;
    }

    const page = pagesContainer.find((p) => p.path === path);

    if (!page) {
      return fallback;
    }

    return wrapComponentFactory(page.component, styledTheme)({ fallback });
  };

  // Mirrors findPage's holding/fallback semantics: blank while the remote theme is still
  // loading, the caller's fallback once resolved with no matching or render-capable entry.
  const findCustomComponent = (
    registry: CustomComponentsRegistry | undefined,
    id: string,
    styledTheme: RemoteTheme,
    fallback: React.FC<any>
  ): React.FC<any> => {
    if (effectiveModuleLoadError) {
      return fallback;
    }

    // Precedes `skip` so unprewarmed SSR and initial client render both hold blank while pending.
    if (effectiveIsPendingLoad) {
      return () => <></>;
    }

    if (skip) {
      return fallback;
    }

    if (!registry) {
      return () => <></>;
    }

    // Cast to Partial -- same as findCarouselTitle's registry lookup above -- because
    // noUncheckedIndexedAccess isn't on and a lookup by an untrusted/arbitrary id can genuinely
    // miss at runtime, which the bare indexed-access type would otherwise hide.
    const entry = (registry as Partial<CustomComponentsRegistry>)[id];
    if (!entry?.component) {
      return fallback;
    }

    return wrapComponentFactory(entry.component, styledTheme)({ fallback });
  };

  const isRemoteThemeResolved = !effectiveIsPendingLoad && !effectiveIsLoading;

  const exposedRemoteThemeModule = useMemo(() => {
    if (isIsolatedPlusCheckoutPage) {
      return { default: undefined };
    }

    if (isStorefrontCheckoutPage) {
      const GlobalStyles = remoteThemeModule.default?.GlobalStyles;
      return { default: GlobalStyles ? { GlobalStyles } : undefined };
    }

    return remoteThemeModule;
  }, [isIsolatedPlusCheckoutPage, isStorefrontCheckoutPage, remoteThemeModule]);

  const getCommerceComponents = useCallback(
    (styledTheme: RemoteTheme = null): CommerceComponents => {
      if (!customizationProvider) {
        return resolvedDefaultCommerceComponents;
      }

      if (isStorefrontCheckoutPage) {
        return {
          ...resolvedDefaultCommerceComponents,
          components: {
            ...resolvedDefaultCommerceComponents.components,
            // Preserve pre-existing root-theme styling on Pro checkout without handing
            // GlobalStyles the commerce bridge or exposing any generic registry behavior.
            GlobalStyles: wrapComponentFactory(remoteThemeModule.default?.GlobalStyles, styledTheme, undefined, false),
          },
          events: undefined,
          isRemoteThemeResolved,
          modulePath: customizationProvider.modulePath,
          themeIdentity,
          metaFields: null,
        };
      }

      return {
        components: {
          StoreFrontHeader: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontHeader,
            styledTheme,
            STOREFRONT_HEADER_SKELETON_MIN_HEIGHT
          ),
          StoreFrontMeta: wrapComponentFactory(remoteThemeModule.default?.StoreFrontMeta, styledTheme),
          StoreFrontNavigation: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontNavigation,
            styledTheme,
            STOREFRONT_NAVIGATION_SKELETON_MIN_HEIGHT
          ),
          StoreFrontFooter: wrapComponentFactory(remoteThemeModule.default?.StoreFrontFooter, styledTheme),
          GlobalStyles: wrapComponentFactory(remoteThemeModule.default?.GlobalStyles, styledTheme),
          StoreFrontHero: wrapComponentFactory(remoteThemeModule.default?.StoreFrontHero, styledTheme),
          StoreFrontLoyaltyWidget: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontLoyaltyWidget,
            styledTheme
          ),
          StoreFrontLoginWidget: wrapComponentFactory(remoteThemeModule.default?.StoreFrontLoginWidget, styledTheme),
          StoreFrontCategoryTiles: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontCategoryTiles,
            styledTheme
          ),
          StoreFrontSpecialsCategoryTiles: wrapComponentFactory(
            remoteThemeModule.default?.StoreFrontSpecialsCategoryTiles,
            styledTheme
          ),
          ListPageHero: wrapComponentFactory(remoteThemeModule.default?.ListPageHero, styledTheme),
          ProductDetailsPrimary: wrapComponentFactory(remoteThemeModule.default?.ProductDetailsPrimary, styledTheme),
          ProductDetailsMeta: wrapComponentFactory(remoteThemeModule.default?.ProductDetailsMeta, styledTheme),
          ProductCard: wrapComponentFactory(remoteThemeModule.default?.ProductCard, styledTheme),
          ProductCardListItem: wrapComponentFactory(remoteThemeModule.default?.ProductCardListItem, styledTheme),
          FilterSidePanel: wrapComponentFactory(remoteThemeModule.default?.FilterSidePanel, styledTheme),
          SpecialsPageHeader: wrapComponentFactory(remoteThemeModule.default?.SpecialsPageHeader, styledTheme),
          SpecialsCard: wrapComponentFactory(remoteThemeModule.default?.SpecialsCard, styledTheme),
        },
        findCarouselTitle: ({ carouselId, fallback = () => <></> }) =>
          findCarouselTitle(remoteThemeModule.default?.StoreFrontCarouselTitles, carouselId, styledTheme, fallback),
        findPage: ({ path, fallback = () => <></> }) =>
          findPage(remoteThemeModule.default?.RouteablePages, path, fallback, styledTheme),
        // `isSwitchingTheme` forces both of these to their "not loaded yet" shape for the
        // one render where `remoteThemeModule` still holds the PREVIOUS target's data -- see its
        // own doc comment above -- so `themeIdentity` and `customComponents` never describe two
        // different themes in the same returned `CommerceComponents`.
        findCustomComponent: ({ id, fallback = () => <></> }) =>
          findCustomComponent(
            isSwitchingTheme ? undefined : remoteThemeModule.default?.CustomComponents,
            id,
            styledTheme,
            fallback
          ),
        customComponents: validateCustomComponentsRegistry(
          isSwitchingTheme ? undefined : remoteThemeModule.default?.CustomComponents
        ),
        iterables: {
          StoreFrontCarouselInterstitials: wrapIterables(
            remoteThemeModule.default?.StoreFrontCarouselInterstitials,
            styledTheme
          ),
          CategoryPageInterstitials: wrapCategoryIterables(
            remoteThemeModule.default?.CategoryPageInterstitials,
            styledTheme
          ),
          CategoryPageSlots: wrapCategoryIterables(remoteThemeModule.default?.CategoryPageSlots, styledTheme),
          CategoryPageBelowList: wrapCategoryBelowList(remoteThemeModule.default?.CategoryPageBelowList, styledTheme),
          CategoryPageSubheaders: getCategorySubheaders(remoteThemeModule.default?.CategoryPageSubheaders),
        },
        pages: ({ fallback = () => <></> } = {}) =>
          wrapPages(remoteThemeModule.default?.RouteablePages, styledTheme, fallback),
        events: remoteThemeModule.default?.events,
        isRemoteThemeResolved,
        modulePath: customizationProvider.modulePath,
        themeIdentity,
        metaFields: null,
        themeConfig: {
          customTagFilters: validateCustomTagFilterConfigs<CustomTagFilterConfig>(
            remoteThemeModule.default?.enableCustomTagFilters ?? []
          ),
          customSmartTagFilters: validateCustomTagFilterConfigs<CustomSmartTagFilterConfig>(
            remoteThemeModule.default?.enableCustomSmartTagFilters ?? []
          ),
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      remoteThemeModule,
      customizationProvider,
      isRemoteThemeResolved,
      isStorefrontCheckoutPage,
      isSwitchingTheme,
      themeIdentity,
    ]
  );
  return {
    getCommerceComponents,
    remoteThemeModule: exposedRemoteThemeModule,
    modulePath: customizationProvider?.modulePath ?? '',
    isRemoteThemeResolved,
    shouldProvideCommerceBridge: Boolean(customizationProvider?.modulePath) && !isStorefrontCheckoutPage,
  };
}
